import { useI18n } from '../i18n.tsx';
import { formatLocalHM, slotStatusFor, type IncidentLike } from '../dates.ts';

interface SlotInfo {
  slotStart: Date;
  slotEnd: Date;
  status: 200 | 500 | null;
}

interface TimelineRowProps {
  incidents: IncidentLike[];
  fromHour: number;
  toHour: number;
  onCellClick?: ((slot: SlotInfo) => void) | undefined;
}

function TimelineRow({ incidents, fromHour, toHour, onCellClick }: TimelineRowProps) {
  const { t, lang } = useI18n();
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  const cells: JSX.Element[] = [];
  for (let min = fromHour * 60; min < toHour * 60; min += 5) {
    const slotStart = new Date(day.getTime() + min * 60_000);
    const slotEnd = new Date(slotStart.getTime() + 5 * 60_000);
    const status = slotStatusFor(incidents, slotStart, slotEnd);
    const cls =
      status === 500 ? ' bg-rose-500' : status === 200 ? ' bg-emerald-500' : ' bg-slate-300';
    const tip = `${
      status === 500
        ? t('legend.incident')
        : status === 200
        ? t('legend.ok')
        : t('legend.none')
    } • ${formatLocalHM(slotStart, lang)} → ${formatLocalHM(slotEnd, lang)}`;
    cells.push(
      <div
        key={min}
        className={`stick clickable${cls}`}
        title={tip}
        onClick={(e) => {
          e.stopPropagation();
          onCellClick?.({ slotStart, slotEnd, status });
        }}
      />,
    );
  }
  return <div className="grid-144">{cells}</div>;
}

export type { SlotInfo };

export interface TimelineProps {
  incidents: IncidentLike[];
  onCellClick?: (slot: SlotInfo) => void;
}

export function Timeline({ incidents, onCellClick }: TimelineProps) {
  const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <div
      className="space-y-3"
      onClick={stopAll}
      onMouseDown={stopAll}
      onTouchStart={stopAll}
    >
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ minWidth: 900 }} className="w-full">
          <div className="flex justify-between text-[10px] text-slate-500 mb-1">
            <span>00:00:00</span>
            <span>12:00:00</span>
          </div>
          <TimelineRow
            incidents={incidents}
            fromHour={0}
            toHour={12}
            onCellClick={onCellClick}
          />
        </div>
      </div>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ minWidth: 900 }} className="w-full">
          <div className="flex justify-between text-[10px] text-slate-500 mb-1">
            <span>12:00:00</span>
            <span>00:00:00</span>
          </div>
          <TimelineRow
            incidents={incidents}
            fromHour={12}
            toHour={24}
            onCellClick={onCellClick}
          />
        </div>
      </div>
    </div>
  );
}
